import { useMarket, useTranslation } from "@ferrivia/i18n";

/* Two very different attributions, and they must not read the same:
   `labCode` is set exactly when the draw was actually processed by a lab
   (today always Arvoterveys — see @ferrivia/api-contract's LAB_CODES).
   A self-reported result has no labCode, so its range is a bare "Viitearvo"
   with no lab attribution — the lookup fell back to DEFAULT_LAB_CODE's
   catalog on the server, but nothing here can pretend Arvoterveys saw a
   sample it never received.

   Split into parts so the reading line and the explainer sheet render the
   same range from one source: the sheet lays it out as a labelled row, the
   card joins it back into a line. `range` stays the display string the
   server resolved — never a pair of numbers something could compare a
   measurement against. */
export interface RangeParts {
  /** How the range is attributed, or the no-range note when there is none. */
  term: string;
  /** The range as the lab reported it, or null when it reported none. */
  range: string | null;
  /** The lab that ran the sample, or null when nothing did. */
  source: string | null;
}

/* The four strings this wording needs, already resolved.

   `rangeParts` used to take a `t` instead, which quietly made every caller
   responsible for passing one bound to the right namespace — and once this
   copy moved into @ferrivia/lab-ui's own `labs` namespace, a `t` from the
   `client` or `clinic` namespace missed every key and fell through to the
   Finnish inline defaults. That is invisible in the Finnish client app and
   wrong everywhere else: the clinic console rendered "Viitealue" inside
   English chrome. Taking resolved strings makes the function pure, keeps the
   namespace decision in one place (`useRangeCopy` below), and leaves nothing
   for a caller to get wrong. */
export interface RangeVocabulary {
  /** Shown when the lab reported no range for this measurement. */
  noRange: string;
  /** How a lab-processed range is introduced. */
  labTerm: string;
  /** How an unverified, self-reported range is introduced. */
  selfReportedTerm: string;
  /** The lab's name, for the one case it may be claimed. */
  labName: string;
}

/* Whether the laboratory that ran the sample may be named at all.

   Client-facing surfaces say no (Ville, 2026-09-05): a Ferrivia client did
   not choose a laboratory and the name tells them nothing. Clinic-facing
   surfaces say yes — to a clinician the assay's source is material, since
   which lab ran it is what the reference range belongs to. So this is a
   property of the surface, not of the result, and each caller states its
   own posture rather than the package guessing.

   The `labTerm`/`selfReportedTerm` split is unaffected either way: that
   wording says whether a range was reported by a laboratory or entered by
   the client, which is true and useful on both surfaces, and it names no
   lab. On the client the same distinction is also carried, more visibly, by
   the "Itse ilmoitettu" marker beside the result.

   The default is NOT to attribute, so that a caller who says nothing fails
   in the safe direction: a new client-facing surface that forgets to pass
   this cannot silently put a laboratory's name back in front of a client,
   which is the regression the rule exists to prevent. Naming a lab is the
   deliberate act, and the clinic console says so explicitly. */
export interface RangeAttribution {
  attributeLab: boolean;
}

export function rangeParts(
  vocab: RangeVocabulary,
  refText: string | null,
  labCode: string | null,
  { attributeLab }: RangeAttribution = { attributeLab: false },
): RangeParts {
  if (refText === null) {
    return { term: vocab.noRange, range: null, source: null };
  }
  return labCode
    ? { term: vocab.labTerm, range: refText, source: attributeLab ? vocab.labName : null }
    : { term: vocab.selfReportedTerm, range: refText, source: null };
}

/** The one-line form a reading carries under its value.

    `unit` is optional because the two surfaces want different densities: the
    client states it ("Viitealue: 15–150 µg/L") so the range is readable on
    its own, while the clinic's table row omits it, having just shown it in
    the value column. Passing a unit also switches the separator to a colon,
    which is the client's house wording for this line. */
export function rangeLine(parts: RangeParts, unit?: string): string {
  if (parts.range === null) return parts.term;
  const head = unit === undefined ? `${parts.term} ${parts.range}` : `${parts.term}: ${parts.range} ${unit}`;
  return parts.source === null ? head : `${head} · ${parts.source}`;
}

export interface RangeCopy {
  /** The range as its labelled parts, for a layout that words them
      separately (the client's explainer sheet). */
  partsFor: (refText: string | null, labCode: string | null) => RangeParts | null;
  /** The one-line form a reading carries under its value. Pass the analyte's
      unit to have it stated in the line. */
  lineFor: (refText: string | null, labCode: string | null, unit?: string) => string | undefined;
}

/* Bound to this package's own `labs` namespace, which is the point — see
   RangeVocabulary above. Every component wording a range goes through here;
   the pure functions above exist for tests and for anywhere there is no
   React context to hang a hook on. */
export function useRangeCopy(attribution: RangeAttribution = { attributeLab: false }): RangeCopy {
  const { t } = useTranslation("labs");
  const market = useMarket();
  const vocab: RangeVocabulary = {
    noRange: t("noRange", "Ei viitearvoa saatavilla"),
    labTerm: t("rangeTerm.lab", "Viitealue"),
    selfReportedTerm: t("rangeTerm.selfReported", "Viitearvo"),
    labName: t("labName", "Arvoterveys"),
  };
  // The US server returns every refText as null; saying "no range available"
  // there would imply one was expected.
  if (market === "us") {
    return { partsFor: () => null, lineFor: () => undefined };
  }
  return {
    partsFor: (refText, labCode) => rangeParts(vocab, refText, labCode, attribution),
    lineFor: (refText, labCode, unit) => rangeLine(rangeParts(vocab, refText, labCode, attribution), unit),
  };
}
